import { InputNumber } from "antd";
import { InputNumberProps } from "antd";
import classNames from "classnames";
import { FieldWrapper } from "components/UI/Table/UI/InteractiveCell/components/FieldWrapper.tsx";
import { WithSaveButton } from "components/UI/Table/UI/InteractiveCell/components/WithSaveButton.tsx";
import { useInteractiveCell } from "components/UI/Table/UI/InteractiveCell/hooks/useInteractiveCell.ts";
import { FieldProps } from "components/UI/Table/UI/InteractiveCell/miscs/types.ts";
import { Controller, FieldValues } from "react-hook-form";
import { CurrencyCode, formatCurrency } from "utils/formatCurrency";

type Props<T extends FieldValues> = InputNumberProps &
    FieldProps<T> & {
        currencyCode?: CurrencyCode | string | null;
    };

export const WithMoney = <T extends FieldValues = FieldValues>({ formControl, currencyCode, ...props }: Props<T>) => {
    const { onChange, onSave, value, className, ...restProps } = props;

    const {
        setControlledValue,
        handleOnSaveSideEffects,
        resolvedValue,
        isFormControlled,
        handleFocus,
        handleBlur,
        fieldRef,
        error,
    } = useInteractiveCell({
        formControl,
        value,
    });

    const handleChange: InputNumberProps["onChange"] = (value) => {
        setControlledValue(value);
        onChange?.(value);
    };

    const handleSave = () => {
        handleOnSaveSideEffects();
        onSave?.(resolvedValue);
    };

    // Format the display value with currency
    const displayValue =
        resolvedValue !== null && resolvedValue !== undefined
            ? formatCurrency(Number(resolvedValue), currencyCode as CurrencyCode)
            : "-";

    return (
        <FieldWrapper displayValue={displayValue} ref={fieldRef} isError={!!error} errorMessage={error?.message as string}>
            <WithSaveButton onSave={handleSave}>
                {isFormControlled && formControl ? (
                    <Controller
                        name={formControl.name}
                        control={formControl.control}
                        render={({ field }) => (
                            <InputNumber
                                {...restProps}
                                className={classNames(className, "!w-full")}
                                value={field.value}
                                onChange={(val) => {
                                    field.onChange(val);
                                    onChange?.(val);
                                }}
                                onBlur={() => {
                                    field.onBlur();
                                    handleBlur();
                                }}
                                onFocus={handleFocus}
                            />
                        )}
                    />
                ) : (
                    <InputNumber
                        {...restProps}
                        onChange={handleChange}
                        className={classNames(className, "!w-full")}
                        value={resolvedValue as InputNumberProps["value"]}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                    />
                )}
            </WithSaveButton>
        </FieldWrapper>
    );
};
